<template>
    <div>
        <el-popover
            v-click-outside="hideSuggestionsPopover"
            placement="bottom-start"
            trigger="manual"
            v-model="showSuggestionsPopover">
            <ul class="el-scrollbar__view el-select-dropdown__list typeahead overscroll-none scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-indigo-200">
                <li
                    v-if="searchResults.length > 0"
                    v-for="(contact, index) in searchResults"
                    :key="index"
                    @mousedown="contactFromSearch(contact)"
                    class="el-select-dropdown__item select-none"
                    :class="{'selected hover': index == searchSelection}">
                    <span>{{ userSearchRequest ? contact.first_name : contact.first_names }} {{ contact.last_name }}
                        <span
                            class="label label-light-info ml-4"
                            v-if="contact.pivot">
                            {{ contact.pivot.role | capitalize }}
                        </span>
                    </span>
                    <small class="d-block">{{ contact.email }}</small>
                </li>

                <li v-if="searchResults.length <= 0"
                    class="el-select-dropdown__item">
                    <span>No matching contacts</span>
                </li>
            </ul>

            <div slot="reference" class="input-group flex flex-nowrap">
                <div v-if="showPrefix" class="input-group-prepend rounded-r-none" :class="inputPrefixClass">{{ inputPrefix }}</div>

                <div style="border-bottom-left-radius: 0; border-top-left-radius: 0"
                     :class="[inputBoxHeight !== null ?  inputBoxHeight : 'h-8', inputBoxClass,
                     'flex border-l-0 w-full overflow-y-auto overflow-x-hidden overscroll-none scrollbar-thin scrollbar-track-gray-100 scrollbar-thumb-indigo-200']">
                    <span
                        class="badge badge-pill badge-light text-xs mr-1 mb-1 py-1 h-5 flex items-center max-w-min"
                        v-for="(contact, index) in contacts"
                        :key="index">
                        <span v-if="!showFullName">{{ contact.email }}</span>
                        <span v-if="showFullName && !userSearchRequest">{{ contact.full_name }}</span>
                        <span v-if="showFullName && userSearchRequest">{{ contact.name }}</span>
                        <i href="#"
                           class="contacts-input-remove mdi mdi-close-circle hover:text-brand cursor-pointer transition duration-300 ml-1.5"
                           @click.prevent="removeContact(index)">
                        </i>
                    </span>

                    <input
                        type="text"
                        class="w-full ring-0 border-none flex-1 bg-transparent rounded-md p-0 m-0 min-h-full"
                        :placeholder="placeholderText"
                        v-model="input"
                        @keydown.enter.prevent="contactFromInput"
                        @keydown.tab="contactFromInput"
                        @keydown.188.prevent="contactFromInput"
                        @keydown.space.prevent="contactFromInput"
                        @keydown.8.stop="removeLastContact"
                        @keyup.esc="ignoreSearchResults"
                        @keyup.down="selectNextContact"
                        @keyup.up="selectPreviousContact"
                        @keyup="searchContact"
                        @focus="searchContact"
                        @blur="contactFromInput"
                        @value="contacts"
                    />
                </div>
            </div>
        </el-popover>
    </div>
</template>

<script>
export default {
    props: {
        inputPrefix: {
            type: String,
            default: "Email: "
        },
        value: {
            type: [Array, String],
            default: () => {
                return [];
            }
        },
        typeahead: {
            type: Boolean,
            default: false
        },
        typeaheadActivationThreshold: {
            type: Number,
            default: 1
        },
        typeaheadMaxResults: {
            type: Number,
            default: 0
        },
        placeholder: {
            type: String,
            default: " Enter email address"
        },
        limit: {
            type: Number,
            default: 0
        },
        deleteOnBackspace: {
            type: Boolean,
            default: true
        },
        allowDuplicates: {
            type: Boolean,
            default: false
        },
        suggestionsApi: {
            type: String,
            default: ""
        },
        emailId: {
            required: false,
            type: Number,
            default: null
        },
        toValue: {
            type: [Object, String],
            default: () => {
                return {};
            },
            description: "To email object"
        },
        elementId: {
            type: String,
            default: "",
            description: "Email input id"
        },
        assignedEmails: {
            type: Array,
            default: () => {
                return [];
            },
            required: false,
            description: "Email ids which already have been taken in other fields"
        },
        visibility: {
            type: Boolean,
            default: null,
            description: "Email input visibility status"
        },
        showPrefix: {
            type: Boolean,
            default: true,
            description: "Boolean to determine if the prefix label should be shown"
        },
        showFullName: {
            type: Boolean,
            default: false,
            description: "Boolean to determine if the full name of the contact should be shown instead of the email"
        },
        userSearchRequest: {
            type: Boolean,
            default: false,
            description: "Boolean to indicate if the component is used for a user-search request instead of contacts as default"
        },
        inputBoxHeight: {
            type: String,
            default: null,
            description: "Tailwind class string to modify input box height"
        },
        inputPrefixClass: {
            required: false,
            type: String,
            default: "input-group-text input-group-prepend-fixed"
        },
        inputBoxClass: {
            required: false,
            type: String,
            default: "input-normal rounded-right flex-wrap items-center h-full"
        },
    },

    data () {
        return {
            contacts: [],
            input: "",
            oldInput: "",
            searchResults: [],
            searchSelection: 0,
            showSuggestionsPopover: false
        };
    },

    computed: {
        placeholderText () {
            if (this.limitReached()) {
                return "";
            }
            return this.placeholder;
        },
        disableInput () {
            return this.limitReached();
        },
    },

    watch: {
        contacts () {
            // Update the bound v-model value
            this.$emit("input", this.contacts);
        },
        value () {
            this.contactsFromValue();
        },
        searchResults () {
            this.searchSelection = 0;
        },
        "toValue": function (newValue, oldValue) {
            this.unsetDefaultCCs(oldValue);
            this.setCCDefaultContacts(false);
        },
    },

    created () {
        this.contactsFromValue();
    },

    methods: {
        setCCDefaultContacts (contact) {
            let targetedContact = contact !== false ? contact : this.toValue;
            if (this.elementId === "cc-email" && this.visibility && targetedContact.always_cc_other) {
                let otherCcEmails = targetedContact.email_other.split(",");
                _.forEach(otherCcEmails, (ccEmail) => {
                    this.contactFromSearch(ccEmail.trim());
                });
            }
        },
        unsetDefaultCCs (oldToField) {
            if (_.isEmpty(oldToField)) {
                return false;
            }

            if (_.isUndefined(oldToField.email_other) || oldToField.email_other === null) {
                return false;
            }

            let otherCcEmails = oldToField.email_other.split(",");
            _.forEach(otherCcEmails, ccEmail => {
                let emailIndex = _.findIndex(this.contacts, contact => {
                    return contact.email === ccEmail.trim();
                });
                if (emailIndex !== -1) {
                    this.removeContact(emailIndex);
                }
            });

        },
        limitReached () {
            return this.limit > 0 && this.contacts.length >= this.limit;
        },
        contactFromInput (e) {
            let text = this.input.trim();
            if (!text.length) {
                return false;
            }
            this.addContact(text);
            this.input = "";

            if (this.limitReached() || this.searchResults.length === 0) {
                this.hideSuggestionsPopover();
            }
        },
        contactFromSearch (contact) {
            this.searchResults = [];
            this.input = "";
            this.oldInput = "";
            this.addContact(contact);
            this.hideSuggestionsPopover();
        },
        addContact (contact) {
            // Check if the limit has been reached
            if (this.limit > 0 && this.contacts.length >= this.limit) {
                return false;
            }

            if (_.isObject(contact) && !_.isUndefined(contact.id) && this.contacts.indexOf(contact) === -1) {
                return this.addValidContact(contact);
            }

            if (this.contacts.findIndex(c => c.email === contact) === -1) {
                return this.addContactByEmail(contact);
            }
        },
        addValidContact (contact) {
            if (!this.validate(contact.email)) {
                return false;
            }
            this.contacts.push(contact);
            this.setCCDefaultContacts(contact);
            this.contactAdded();
            return true;
        },
        addContactByEmail (contact) {
            if (!this.validate(contact)) {
                return false;
            }
            this.contacts.push({email: contact});
            this.contactAdded();
            return true;
        },
        contactAdded () {
            this.$emit("contact-added");
        },
        removeLastContact (e) {
            if (!e.target.value.length && this.deleteOnBackspace) {
                this.removeContact(this.contacts.length - 1);
                e.preventDefault();
            }
        },
        removeContact (index) {
            this.contacts.splice(index, 1);
            this.searchResults = [];
        },
        searchContact (e) {
            this.showSuggestionsPopover = !this.limitReached();

            if (!this.typeahead) {
                return;
            }

            if (_.isEqual(this.oldInput, this.input) && _.isEmpty(this.input) && this.searchResults.length > 0) {
                return;
            }

            if (this.limitReached()) {
                this.input = "";
            }

            let input = this.input.trim();
            let exceptEmails = this.assignedEmails.length > 0 ? this.assignedEmails : [];

            axios.get(this.suggestionsApi,
                {
                    params: {
                        filter_text: input,
                        email_id: this.emailId,
                        except_emails: exceptEmails
                    }
                }
            )
            .then(response => {
                this.searchResults = response.data !== undefined ? response.data : [];
                this.searchResults = response.data.filter(emailObject => !this.assignedEmails.includes(emailObject.email));
                this.oldInput = this.input;
            });

        },
        ignoreSearchResults () {
            this.searchResults = [];
            this.searchSelection = 0;
        },
        /**
         * Clear the list of selected contacts
         */
        clearContacts () {
            this.contacts.splice(0, this.contacts.length);
        },
        /**
         * Replace the currently selected contacts with the contacts from the value
         */
        contactsFromValue () {
            if (this.value && this.value.length) {
                let contacts = Array.isArray(this.value)
                    ? this.value
                    : this.value.split(",");
                if (this.contacts == contacts) {
                    return;
                }
                this.clearContacts();
                for (let val of contacts) {
                    this.addContact(val);
                }
            } else {
                if (this.contacts.length === 0) {
                    return;
                }
                this.clearContacts();
            }
        },
        selectNextContact () {
            if ((this.searchSelection + 1) < this.searchResults.length) {
                this.searchSelection++;
            }
        },
        selectPreviousContact () {
            if ((this.searchSelection - 1) >= 0) {
                this.searchSelection--;
            }
        },
        validate (input) {
            let validationPassed = this.validateEmail(input);
            if (!validationPassed) {
                this.hideSuggestionsPopover();
                this.$emit('validation-error');
                return false;
            }

            return true;
        },
        hideSuggestionsPopover () {
            this.showSuggestionsPopover = false;
        },
    },
};
</script>

<style>

.typeahead > span {
    cursor: pointer;
    margin-right: 0.3rem;
}

.typeahead .el-select-dropdown__item {
    height: 50px;
}

.typeahead .el-select-dropdown__item span {
    line-height: 0.5;
}

.typeahead .el-select-dropdown__item small {
    line-height: 0.5;
}
</style>
